import { FC, useRef, useEffect, useMemo, useState } from 'react';
import { LongreadBlockWrapper } from '../../styles/LongreadBlockRenderer.styles';
import styled from 'styled-components';
import { LayoutConst } from 'core';
import { useBreakpoint } from '@brg/ui';

interface VideoBlockProps {
  block: any;
  backgroundColor?: string;
  isActive?: boolean;
}

const checkVideoType = (link: string): string => {
  if (!link) return 'nodata';

  const kscType = /(?:https?:\/\/(?:www\.)?kinescope\.io\/(?:embed\/)?[\w\-]+)/;
  const vk = /^.*(vk\.(com|ru)|vkvideo\.(com|ru)).*$/;
  const scType = /^.*(sound).*$/;
  const vType = /^.*(vime).*$/;
  const rtType = /^.*(rutube).*$/;
  const ytType = /^.*(youtube\.com|youtu\.be).*$/;
  const frame = /<iframe\s+[^>]*?><\/iframe>/gm;

  if (link.match(rtType) !== null && link.match(frame) !== null) {
    return 'rt';
  }

  if (kscType.test(link)) {
    return 'ksc';
  }

  if (link.match(vk) !== null && link.match(frame) !== null) {
    return 'vk';
  }

  if (link.match(scType) !== null) {
    return 'sc';
  }

  if (link.match(vType) !== null) {
    return 'v';
  }

  if (link.match(ytType) !== null) {
    return 'yt';
  }

  return 'nodata';
};

// Функция для получения ID видео Kinescope
const getKinescopeVideoId = (url: string): string => {
  const kscType = /(?:https?:\/\/(?:www\.)?kinescope\.io\/(?:embed\/)?|<iframe[^>]+src=["']?https?:\/\/kinescope\.io\/embed\/)([\w\-]+)/;
  return url?.match(kscType)?.[1] || '';
};

const getYouTubeVideoId = (urlOrIframe: string): string => {
  if (!urlOrIframe) return '';

  const iframeSrc = urlOrIframe.match(/src="(.*?)"/)?.[1];
  const raw = iframeSrc || urlOrIframe;

  const embedId = raw.match(/youtube\.com\/embed\/([\w-]{6,})/)?.[1];
  if (embedId) return embedId;

  const watchId = raw.match(/[?&]v=([\w-]{6,})/)?.[1];
  if (watchId) return watchId;

  const shortId = raw.match(/youtu\.be\/([\w-]{6,})/)?.[1];
  if (shortId) return shortId;

  const shortsId = raw.match(/youtube\.com\/shorts\/([\w-]{6,})/)?.[1];
  if (shortsId) return shortsId;

  return '';
};


const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  pointer-events: auto;

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 12px;
    box-sizing: border-box;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  max-width: 1920px;
  max-height: 1080px;
  position: relative;
  pointer-events: auto;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    max-width: 1440px;
    max-height: 810px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: 100%;
    max-height: none;
    height: auto;
    aspect-ratio: 16 / 9;
  }
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
    pointer-events: auto;
    cursor: pointer;
  }
  
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    pointer-events: auto;
    cursor: pointer;
  }
`;
const MobileSwipeOverlay = styled.div<{ $active: boolean }>`
  @media (max-width: ${LayoutConst.screen.s}px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
    background: transparent;
    -webkit-tap-highlight-color: transparent;
  }

  @media (min-width: ${LayoutConst.screen.s + 1}px) {
    display: none;
  }
`;

export const VideoBlock: FC<VideoBlockProps> = ({ block, isActive }) => {
  const videoLink = block.text || '';
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoType = useMemo(() => checkVideoType(videoLink), [videoLink]);
  const iframeSrc = (src: string) => (isActive ? src : 'about:blank');
  const isMobile = useBreakpoint(LayoutConst.screen.s);
  const [overlayActive, setOverlayActive] = useState(true);
  const overlayTouchStartRef = useRef({ x: 0, y: 0 });
  const overlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isActive) {
      setOverlayActive(true);
    }
  }, [isActive]);

  useEffect(() => {
    return () => {
      if (overlayTimerRef.current !== null) {
        clearTimeout(overlayTimerRef.current);
      }
    };
  }, []);

  const handleOverlayTouchStart = (e: React.TouchEvent) => {
    overlayTouchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleOverlayTouchEnd = (e: React.TouchEvent) => {
    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - overlayTouchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - overlayTouchStartRef.current.y);

    if (deltaX < 10 && deltaY < 10) {
      if (overlayTimerRef.current !== null) {
        clearTimeout(overlayTimerRef.current);
      }
      setOverlayActive(false);
      overlayTimerRef.current = setTimeout(() => {
        setOverlayActive(true);
        overlayTimerRef.current = null;
      }, 3000);
    }
  };

  useEffect(() => {
    if (isActive || !videoRef.current) {
      return;
    }
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  }, [isActive]);

  const renderVideo = useMemo(() => {
    if (!videoLink || videoType === 'nodata') {
      return null;
    }

    // Kinescope
    if (videoType === 'ksc') {
      const videoId = getKinescopeVideoId(videoLink);
      if (videoId) {
        return (
          <iframe
            src={iframeSrc(`https://kinescope.io/embed/${videoId}?autoplay=0`)}
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer; clipboard-write;"
            frameBorder="0"
            allowFullScreen
          />
        );
      }
    }

    // VK, Rutube - iframe из ссылки
    if (['vk', 'rt'].includes(videoType)) {
      const srcMatch = videoLink.match(/src="(.*?)"/);
      let src = srcMatch ? srcMatch[1] : null;
      if (src) {
        // Убираем autoplay
        src = src.replace('autoplay=1', 'autoplay=0');
        const heightMatch = videoLink.match(/height="\s*(\d+)"/);
        const widthMatch = videoLink.match(/width="\s*(\d+)"/);
        const h = heightMatch ? heightMatch[1] : null;
        const w = widthMatch ? widthMatch[1] : null;

        if (src && h && w) {
          const jsApiParam = videoType === 'rt' ? '' : '&js_api=1';
          return (
            <iframe
              src={iframeSrc(src + jsApiParam)}
              width={w}
              height={h}
              scrolling="no"
              frameBorder="no"
              allow="autoplay"
            />
          );
        }
      }
    }

    if (['v', 'sc'].includes(videoType)) {
      if (videoLink.match(/\.(mp4|webm|ogg|mov)/i)) {
        return (
          <video
            ref={videoRef}
            src={videoLink}
            controls
            style={{ width: '100%', height: '100%' }}
          />
        );
      }

      // Vimeo
      if (videoType === 'v') {
        const vimeoId = videoLink.match(/vimeo\.com\/(\d+)/)?.[1];
        if (vimeoId) {
          return (
            <iframe
              src={iframeSrc(`https://player.vimeo.com/video/${vimeoId}?autoplay=0`)}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          );
        }
      }
    }

    if (videoType === 'yt') {
      const ytId = getYouTubeVideoId(videoLink);
      if (ytId) {
        return (
          <iframe
            src={iframeSrc(`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1&autoplay=0`)}
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer; clipboard-write"
            frameBorder="0"
            allowFullScreen
          />
        );
      }
    }

    if (videoLink.match(/\.(mp4|webm|ogg|mov|avi)/i)) {
      return (
        <video
          ref={videoRef}
          src={videoLink}
          controls
          style={{ width: '100%', height: '100%' }}
        />
      );
    }

    return null;
  }, [videoLink, videoType, isActive]);

  if (!videoLink || !renderVideo) {
    return null;
  }

  return (
    <LongreadBlockWrapper>
      <VideoContainer
        onPointerDownCapture={!isMobile ? (e) => e.stopPropagation() : undefined}
        onMouseDownCapture={!isMobile ? (e) => e.stopPropagation() : undefined}
      >
        <VideoWrapper>
          {renderVideo}
          {isMobile && (
            <MobileSwipeOverlay
              $active={overlayActive}
              onTouchStart={handleOverlayTouchStart}
              onTouchEnd={handleOverlayTouchEnd}
            />
          )}
        </VideoWrapper>
      </VideoContainer>
    </LongreadBlockWrapper>
  );
};
import { FC, useRef, useEffect, useMemo, useState } from 'react';
import { LongreadBlockWrapper } from '../../styles/LongreadBlockRenderer.styles';
import styled from 'styled-components';
import { LayoutConst } from 'core';
import { useBreakpoint } from '@brg/ui';

interface VideoBlockProps {
  block: any;
  backgroundColor?: string;
  isActive?: boolean;
}

const checkVideoType = (link: string): string => {
  if (!link) return 'nodata';

  const kscType = /(?:https?:\/\/(?:www\.)?kinescope\.io\/(?:embed\/)?[\w\-]+)/;
  const vk = /^.*(vk\.(com|ru)|vkvideo\.(com|ru)).*$/;
  const scType = /^.*(sound).*$/;
  const vType = /^.*(vime).*$/;
  const rtType = /^.*(rutube).*$/;
  const ytType = /^.*(youtube\.com|youtu\.be).*$/;
  const frame = /<iframe\s+[^>]*?><\/iframe>/gm;

  if (link.match(rtType) !== null && link.match(frame) !== null) {
    return 'rt';
  }

  if (kscType.test(link)) {
    return 'ksc';
  }

  if (link.match(vk) !== null && link.match(frame) !== null) {
    return 'vk';
  }

  if (link.match(scType) !== null) {
    return 'sc';
  }

  if (link.match(vType) !== null) {
    return 'v';
  }

  if (link.match(ytType) !== null) {
    return 'yt';
  }

  return 'nodata';
};

// Функция для получения ID видео Kinescope
const getKinescopeVideoId = (url: string): string => {
  const kscType = /(?:https?:\/\/(?:www\.)?kinescope\.io\/(?:embed\/)?|<iframe[^>]+src=["']?https?:\/\/kinescope\.io\/embed\/)([\w\-]+)/;
  return url?.match(kscType)?.[1] || '';
};

const getYouTubeVideoId = (urlOrIframe: string): string => {
  if (!urlOrIframe) return '';

  const iframeSrc = urlOrIframe.match(/src="(.*?)"/)?.[1];
  const raw = iframeSrc || urlOrIframe;

  const embedId = raw.match(/youtube\.com\/embed\/([\w-]{6,})/)?.[1];
  if (embedId) return embedId;

  const watchId = raw.match(/[?&]v=([\w-]{6,})/)?.[1];
  if (watchId) return watchId;

  const shortId = raw.match(/youtu\.be\/([\w-]{6,})/)?.[1];
  if (shortId) return shortId;

  const shortsId = raw.match(/youtube\.com\/shorts\/([\w-]{6,})/)?.[1];
  if (shortsId) return shortsId;

  return '';
};


const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  pointer-events: auto;

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 12px;
    box-sizing: border-box;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  max-width: 1920px;
  max-height: 1080px;
  position: relative;
  pointer-events: auto;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    max-width: 1440px;
    max-height: 810px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: 100%;
    max-height: none;
    height: auto;
    aspect-ratio: 16 / 9;
  }
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
    pointer-events: auto;
    cursor: pointer;
  }
  
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    pointer-events: auto;
    cursor: pointer;
  }
`;
const MobileSwipeOverlay = styled.div<{ $active: boolean }>`
  @media (max-width: ${LayoutConst.screen.s}px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
    background: transparent;
    -webkit-tap-highlight-color: transparent;
  }

  @media (min-width: ${LayoutConst.screen.s + 1}px) {
    display: none;
  }
`;

export const VideoBlock: FC<VideoBlockProps> = ({ block, isActive }) => {
  const videoLink = block.text || '';
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoType = useMemo(() => checkVideoType(videoLink), [videoLink]);
  const iframeSrc = (src: string) => (isActive ? src : 'about:blank');
  const isMobile = useBreakpoint(LayoutConst.screen.s);
  const [overlayActive, setOverlayActive] = useState(true);
  const overlayTouchStartRef = useRef({ x: 0, y: 0 });
  const overlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isActive) {
      setOverlayActive(true);
    }
  }, [isActive]);

  useEffect(() => {
    return () => {
      if (overlayTimerRef.current !== null) {
        clearTimeout(overlayTimerRef.current);
      }
    };
  }, []);

  const handleOverlayTouchStart = (e: React.TouchEvent) => {
    overlayTouchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleOverlayTouchEnd = (e: React.TouchEvent) => {
    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - overlayTouchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - overlayTouchStartRef.current.y);

    if (deltaX < 10 && deltaY < 10) {
      if (overlayTimerRef.current !== null) {
        clearTimeout(overlayTimerRef.current);
      }
      setOverlayActive(false);
      overlayTimerRef.current = setTimeout(() => {
        setOverlayActive(true);
        overlayTimerRef.current = null;
      }, 3000);
    }
  };

  useEffect(() => {
    if (isActive || !videoRef.current) {
      return;
    }
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  }, [isActive]);

  const renderVideo = useMemo(() => {
    if (!videoLink || videoType === 'nodata') {
      return null;
    }

    // Kinescope
    if (videoType === 'ksc') {
      const videoId = getKinescopeVideoId(videoLink);
      if (videoId) {
        return (
          <iframe
            src={iframeSrc(`https://kinescope.io/embed/${videoId}?autoplay=0`)}
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer; clipboard-write;"
            frameBorder="0"
            allowFullScreen
          />
        );
      }
    }

    // VK, Rutube - iframe из ссылки
    if (['vk', 'rt'].includes(videoType)) {
      const srcMatch = videoLink.match(/src="(.*?)"/);
      let src = srcMatch ? srcMatch[1] : null;
      if (src) {
        // Убираем autoplay
        src = src.replace('autoplay=1', 'autoplay=0');
        const heightMatch = videoLink.match(/height="\s*(\d+)"/);
        const widthMatch = videoLink.match(/width="\s*(\d+)"/);
        const h = heightMatch ? heightMatch[1] : null;
        const w = widthMatch ? widthMatch[1] : null;

        if (src && h && w) {
          const jsApiParam = videoType === 'rt' ? '' : '&js_api=1';
          return (
            <iframe
              src={iframeSrc(src + jsApiParam)}
              width={w}
              height={h}
              scrolling="no"
              frameBorder="no"
              allow="autoplay"
            />
          );
        }
      }
    }

    if (['v', 'sc'].includes(videoType)) {
      if (videoLink.match(/\.(mp4|webm|ogg|mov)/i)) {
        return (
          <video
            ref={videoRef}
            src={videoLink}
            controls
            style={{ width: '100%', height: '100%' }}
          />
        );
      }

      // Vimeo
      if (videoType === 'v') {
        const vimeoId = videoLink.match(/vimeo\.com\/(\d+)/)?.[1];
        if (vimeoId) {
          return (
            <iframe
              src={iframeSrc(`https://player.vimeo.com/video/${vimeoId}?autoplay=0`)}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          );
        }
      }
    }

    if (videoType === 'yt') {
      const ytId = getYouTubeVideoId(videoLink);
      if (ytId) {
        return (
          <iframe
            src={iframeSrc(`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1&autoplay=0`)}
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media; gyroscope; accelerometer; clipboard-write"
            frameBorder="0"
            allowFullScreen
          />
        );
      }
    }

    if (videoLink.match(/\.(mp4|webm|ogg|mov|avi)/i)) {
      return (
        <video
          ref={videoRef}
          src={videoLink}
          controls
          style={{ width: '100%', height: '100%' }}
        />
      );
    }

    return null;
  }, [videoLink, videoType, isActive]);

  if (!videoLink || !renderVideo) {
    return null;
  }

  return (
    <LongreadBlockWrapper>
      <VideoContainer
        onPointerDownCapture={!isMobile ? (e) => e.stopPropagation() : undefined}
        onMouseDownCapture={!isMobile ? (e) => e.stopPropagation() : undefined}
      >
        <VideoWrapper>
          {renderVideo}
          {isMobile && (
            <MobileSwipeOverlay
              $active={overlayActive}
              onTouchStart={handleOverlayTouchStart}
              onTouchEnd={handleOverlayTouchEnd}
            />
          )}
        </VideoWrapper>
      </VideoContainer>
    </LongreadBlockWrapper>
  );
};
